// Auth endpoints. Login is live against the backend; the rest of the domain
// (OTP, refresh, reset) gets added here as the flows are wired.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import { setAuth } from '@/redux/auth/authSlice';
import { otpRequested } from '@/redux/otp/otpSlice';
import { phoneOtpKey } from '@/redux/otp/otpKeys';

export interface LoginDto {
  /** Dial code incl. the +, e.g. '+966'. */
  countryCode: string;
  /** National number, digits only. */
  mobileNumber: string;
  /** Plaintext; sent over HTTPS and hashed server-side. (Client-side RSA in
   *  api/crypto.ts is kept for later; not currently applied.) */
  password: string;
}

export interface LoginResult {
  serverPrivateKey: string;
  token: string;
  refreshToken: string;
  /** 'USER_PENDING' → registration OTP not verified; 'PASSWORD_PENDING' → password not set. */
  code?: string;
}

/** Which flow an OTP belongs to. Carried in router state and sent to the API. */
export enum OTPType {
  REGISTER = 'register',
  LOGIN = 'login',
  RESET_PASSWORD = 'reset_password',
}

export interface RegisterDto {
  firstName: string;
  lastName: string;
  /** Optional — backend allows registering without an email. */
  email?: string;
  /** Plaintext; the axios client encrypts the whole payload transparently. */
  password: string;
  /** Dial code incl. the +, e.g. '+966'. */
  countryCode: string;
  /** National number, digits only. */
  mobileNumber: string;
}

/** Register response shape is not finalized backend-side; kept loose for now. */
export type RegisterResult = Record<string, unknown>;

export interface SendOtpDto {
  countryCode: string;
  mobileNumber: string;
  otpType: OTPType;
}

export interface VerifyOtpDto extends SendOtpDto {
  code: string;
}

/** Verify returns a success envelope only (no session tokens). */
export interface VerifyOtpResult {
  /** false → account has no login password yet; user must set one next. */
  hasPassword?: boolean;
  /** Short-lived token proving this OTP was verified. Stored in the auth slice
   *  and sent as the reset-password Authorization token (see routes.ts →
   *  tokenType 'validationKey'). Absent for REGISTER verifies. */
  validationKey?: string;
}

export interface ResetPasswordDto {
  countryCode: string;
  mobileNumber: string;
  /** Plaintext; the axios client encrypts the whole payload transparently. */
  password: string;
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation<LoginResult, LoginDto>({
      query: (body) => ({ ...ROUTES.auth.login, data: body }),
    }),
    register: build.mutation<RegisterResult, RegisterDto>({
      query: (body) => ({ ...ROUTES.auth.register, data: body }),
    }),
    sendOtp: build.mutation<void, SendOtpDto>({
      query: (body) => ({ ...ROUTES.auth.sendOtp, data: body }),
      // Requesting a fresh OTP invalidates any previously issued validationKey.
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        dispatch(setAuth({ validationKey: null }));
        // Only a SENT code starts the resend cooldown — a failed request must
        // leave the button usable (the screen surfaces the error itself).
        try {
          await queryFulfilled;
          dispatch(
            otpRequested({
              key: phoneOtpKey(arg.otpType, arg.countryCode, arg.mobileNumber),
              at: Date.now(),
            }),
          );
        } catch {
          /* no cooldown on a failed send */
        }
      },
    }),
    verifyOtp: build.mutation<VerifyOtpResult, VerifyOtpDto>({
      query: (body) => ({ ...ROUTES.auth.verifyOtp, data: body }),
    }),
    resetPassword: build.mutation<void, ResetPasswordDto>({
      query: (body) => ({ ...ROUTES.auth.resetPassword, data: body }),
      // The validationKey is single-use — drop it once the reset succeeds.
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(setAuth({ validationKey: null }));
        } catch {
          /* leave the key in place so the user can retry */
        }
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useSendOtpMutation,
  useVerifyOtpMutation,
  useResetPasswordMutation,
} = authApi;
